import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BookOpen, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api from "@/lib/api";

export function PageGuideButton({ pageKey, basePath = "/admin" }) {
  const { t, i18n } = useTranslation();
  const lng = (i18n.language || "bs").slice(0, 2);
  const [open, setOpen] = useState(false);
  const [guide, setGuide] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { setGuide(null); api.get(`${basePath}/page-guide/${pageKey}?lang=${lng}`).then((r) => setGuide(r.data)).catch(() => setGuide(false)); };
  useEffect(() => { if (open && guide === null) load(); /* eslint-disable-next-line */ }, [open]);
  const refresh = async () => {
    setBusy(true);
    try { const r = await api.post(`${basePath}/page-guide/${pageKey}/refresh?lang=${lng}`); setGuide(r.data); toast.success(t("refd.guideUpdated")); }
    catch { toast.error("Error"); } finally { setBusy(false); }
  };
  const c = guide && guide.content;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="page-guide-btn"><BookOpen className="mr-1 h-4 w-4" /> {t("refd.guideBtn")}</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[82vh] max-w-2xl overflow-y-auto" data-testid="page-guide-dialog">
        <DialogHeader><DialogTitle>{c?.title || t("refd.guideBtn")}</DialogTitle></DialogHeader>
        {guide === null ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div> :
          guide === false ? <div className="py-6 text-center text-sm text-slate-400">—</div> : (
            <div className="space-y-4" data-testid="page-guide-content">
              {c.intro && <p className="text-sm leading-relaxed text-slate-600">{c.intro}</p>}
              {Array.isArray(c.steps) && c.steps.length > 0 && (
                <ol className="space-y-3">
                  {c.steps.map((s, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan/15 text-xs font-bold text-cyan-dark">{i + 1}</span>
                      <div><div className="text-sm font-semibold text-navy">{s.heading}</div><div className="text-sm text-slate-600">{s.body}</div></div>
                    </li>
                  ))}
                </ol>
              )}
              {Array.isArray(c.tips) && c.tips.length > 0 && (
                <div className="rounded-xl border border-cyan/20 bg-cyan/5 p-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-cyan-dark">{t("refd.guideTips")}</div>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">{c.tips.map((tp, i) => <li key={i}>{tp}</li>)}</ul>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-400">
                <span>{t("refd.guideAuto")}{guide.generated_at ? ` · ${new Date(guide.generated_at).toLocaleDateString()}` : ""}</span>
                <Button size="sm" variant="ghost" onClick={refresh} disabled={busy} data-testid="page-guide-refresh" className="rounded-full">
                  {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />} {t("refd.guideRefresh")}
                </Button>
              </div>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}
